import { DSNoteInput } from "@components/design-system/organisms/DSNoteInput";
import { usePlainNoteEditor } from "@hooks";
import { useWritingAssistant } from "@hooks/use-writing-assistant";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import WritingAssistantFieldActions from "../../../../common/writing-assistant/WritingAssistantFieldActions";
import CustomizationFeedback from "../../../../notes/common/CustomizationFeedback";
import { FIELD_STYLES, TEMPLATE_TYPES } from "../../../constants";

// Single source of truth for section ↔ templateField ↔ dataKey mapping
const SECTION_CONFIG = {
  subjective: {
    templateField: "SUBJECTIVE",
    dataKey: "subjective",
    label: "Subjective",
  },
  objective: {
    templateField: "OBJECTIVE",
    dataKey: "objective",
    label: "Objective",
  },
  assessment: {
    templateField: "ASSESSMENT",
    dataKey: "assessment",
    label: "Assessment",
  },
  plan: { templateField: "PLAN", dataKey: "plan", label: "Plan" },
  freeform: {
    templateField: "CUSTOM",
    dataKey: "content",
    label: "Freeform",
  },
};

// Derived: RHF templateField name → DSNoteInput data key (for noteData conversion)
const TEMPLATE_FIELD_TO_DATA_KEY = Object.fromEntries(
  Object.values(SECTION_CONFIG).map((c) => [
    c.templateField,
    c.dataKey,
  ]),
);

const SOAP_SECTIONS = [
  "subjective",
  "objective",
  "assessment",
  "plan",
];

const TemplateFields = ({
  control,
  templateUuid,
  readOnly = false,
}) => {
  const { setValue, getValues } = useFormContext();
  const usePlainEditor = usePlainNoteEditor();
  const templateFields = useWatch({
    control,
    name: "templateFields",
  });
  const templateType = useWatch({ control, name: "templateType" });

  const {
    aiAssistantEnabled,
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  } = useFlags();

  const isSoap = templateType === TEMPLATE_TYPES.SOAP;
  const sections = isSoap ? SOAP_SECTIONS : ["freeform"];

  // Find RHF index for a section's template field
  const findFieldIndex = (section) => {
    const { templateField } = SECTION_CONFIG[section];
    return templateFields.findIndex(
      (f) => f.fieldName === templateField,
    );
  };

  // Read/write template field values by section name (for cross-field accessors)
  const getFieldValueBySection = (section) => {
    const idx = findFieldIndex(section);
    if (idx === -1) return "";
    return getValues(`templateFields.${idx}.fieldValue`) || "";
  };

  const setFieldValueBySection = (section, value) => {
    const idx = findFieldIndex(section);
    if (idx === -1) return;
    setValue(`templateFields.${idx}.fieldValue`, value, {
      shouldDirty: true,
    });
  };

  const makeWaParams = (section) => ({
    entityType: "template",
    entityUuid: templateUuid,
    fieldName: SECTION_CONFIG[section].templateField,
    getFieldValue: () => getFieldValueBySection(section),
    setFieldValue: (v) => setFieldValueBySection(section, v),
    getOtherFieldValue: (fn) => {
      // fn is a templateField name (e.g. "SUBJECTIVE") from translate-all
      const entry = Object.entries(SECTION_CONFIG).find(
        ([, c]) => c.templateField === fn,
      );
      return entry ? getFieldValueBySection(entry[0]) : "";
    },
    setOtherFieldValue: (fn, v) => {
      const entry = Object.entries(SECTION_CONFIG).find(
        ([, c]) => c.templateField === fn,
      );
      if (entry) setFieldValueBySection(entry[0], v);
    },
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  // Hooks must be called unconditionally — one per possible section
  const waSubjective = useWritingAssistant(
    makeWaParams("subjective"),
  );
  const waObjective = useWritingAssistant(makeWaParams("objective"));
  const waAssessment = useWritingAssistant(
    makeWaParams("assessment"),
  );
  const waPlan = useWritingAssistant(makeWaParams("plan"));
  const waFreeform = useWritingAssistant(makeWaParams("freeform"));

  const writingAssistants = {
    subjective: waSubjective,
    objective: waObjective,
    assessment: waAssessment,
    plan: waPlan,
    freeform: waFreeform,
  };

  // Convert RHF templateFields array → NoteDisplayData object
  const noteData = useMemo(() => {
    const data = {};
    templateFields.forEach((field) => {
      const key = TEMPLATE_FIELD_TO_DATA_KEY[field.fieldName];
      if (key) {
        data[key] = field.fieldValue || "";
      }
    });
    return data;
  }, [templateFields]);

  // Sync DSNoteInput changes back to RHF
  const handleNoteChange = (newData) => {
    templateFields.forEach((field, index) => {
      const key = TEMPLATE_FIELD_TO_DATA_KEY[field.fieldName];
      if (!key) return;
      const newValue = newData[key] || "";
      if (newValue !== field.fieldValue) {
        setValue(`templateFields.${index}.fieldValue`, newValue, {
          shouldDirty: true,
        });
      }
    });
  };

  const showAiActions = aiAssistantEnabled && templateUuid;

  return (
    <>
      <DSNoteInput
        dataTestId="template-fields"
        data={noteData}
        onChange={handleNoteChange}
        disabled={readOnly}
        showPlaceholderAlways
        plainText={usePlainEditor}
      >
        {sections.map((section) => {
          const wa = writingAssistants[section];
          const { label } = SECTION_CONFIG[section];

          return (
            <div key={section} data-hint-field={section}>
              <DSNoteInput.NoteField
                section={section}
                placeholder={
                  FIELD_STYLES[SECTION_CONFIG[section].templateField]
                    ?.placeholder
                }
                showSparkleButton={false}
                showCopyButton={false}
                actions={
                  showAiActions && !wa.diffState ? (
                    <WritingAssistantFieldActions
                      wa={wa}
                      sectionLabel={label}
                      writingAssistant={false}
                    />
                  ) : undefined
                }
                {...(wa.diffState
                  ? {
                      diffState: wa.diffState,
                      onDiffAccept: wa.acceptChanges,
                      onDiffReject: wa.rejectChanges,
                    }
                  : {})}
              />

              {showAiActions &&
                wa.isShowingExtraContent &&
                wa.customizationFeedback.hasFeedbackAccess && (
                  <CustomizationFeedback
                    llmCustomizationUuid={
                      wa.customizationFeedback.llmCustomizationUuid
                    }
                  />
                )}
            </div>
          );
        })}
      </DSNoteInput>
    </>
  );
};

export default TemplateFields;
